import React from "react";
import { motion } from "framer-motion";
import {
  Box,
  Typography,
  Paper,
  TextField,
  InputAdornment
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { BRAND_COLOR } from "@kivunova/kivufrontendcommon";

export type ListPageToolbarProps = {
  title: string;
  icon: React.ReactNode;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  children?: React.ReactNode;
  isMobile?: boolean;
  /** When true, render header and search in two separate cards */
  separateSearchCard?: boolean;
};

const paperSx = {
  p: 2.5,
  borderRadius: 2,
  border: "1px solid",
  borderColor: "divider",
  backgroundColor: "background.paper"
};

const searchFieldSx = {
  borderRadius: 1.5,
  backgroundColor: "action.hover",
  "& fieldset": { borderColor: "transparent" },
  "&:hover fieldset": { borderColor: "divider" },
  "&.Mui-focused fieldset": {
    borderColor: BRAND_COLOR,
    borderWidth: 1
  }
};

const ListPageToolbar: React.FC<ListPageToolbarProps> = ({
  title,
  icon,
  searchValue = "",
  onSearchChange,
  searchPlaceholder,
  children,
  isMobile = false,
  separateSearchCard = false
}) => {
  const headerBlock = (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      gap={2}
      flexDirection={isMobile ? "column" : "row"}
    >
      <Box display="flex" alignItems="center" gap={1.5}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 40,
            height: 40,
            borderRadius: 1.5,
            backgroundColor: `${BRAND_COLOR}14`
          }}
        >
          {icon}
        </Box>
        <Typography variant="h5" fontWeight="700" color="text.primary">
          {title}
        </Typography>
      </Box>

      {children && (
        <Box
          display="flex"
          alignItems="center"
          gap={1.5}
          flexDirection={isMobile ? "column" : "row"}
          flexWrap="wrap"
        >
          {children}
        </Box>
      )}
    </Box>
  );

  const searchBlock = onSearchChange && (
    <Box sx={separateSearchCard ? {} : { mt: 2 }}>
      <TextField
        fullWidth
        size="small"
        placeholder={searchPlaceholder}
        value={searchValue}
        onChange={(e) => onSearchChange(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchOutlinedIcon sx={{ color: "action.active" }} />
            </InputAdornment>
          ),
          sx: searchFieldSx
        }}
      />
    </Box>
  );

  if (separateSearchCard) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Paper
          component={motion.div}
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
          elevation={0}
          sx={paperSx}
        >
          {headerBlock}
        </Paper>
        {searchBlock && (
          <Paper
            component={motion.div}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.35,
              ease: [0.25, 0.46, 0.45, 0.94],
              delay: 0.05
            }}
            elevation={0}
            sx={paperSx}
          >
            {searchBlock}
          </Paper>
        )}
      </Box>
    );
  }

  return (
    <Paper
      component={motion.div}
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
      elevation={0}
      sx={paperSx}
    >
      {headerBlock}
      {searchBlock}
    </Paper>
  );
};

export default ListPageToolbar;
